<template>
  <div class="FormContainerBlock mt-5 grid gap-y-5 font-medium">
    <form :id="form?.key" :key="formKey" :class="['grid gap-y-5', { 'pt-5': noLeadingTitle }]">
      <template v-for="element in displayedElements" :key="element.key">
        <FormText
          v-if="
            element.contentType === FormElementsContentTypes.TextboxElementBlock ||
            element.contentType === FormElementsContentTypes.NumberElementBlock
          "
          v-model="inputModels[element.key]"
          :form-element="element"
          :product="product"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormExpandableOptionalText
          v-if="element.contentType === FormElementsContentTypes.ExpandableOptionalTextboxElementBlock"
          :model="inputModels[element.key]"
          :form-element="element as ExpandableOptionalTextElement"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormTextArea
          v-if="element.contentType === FormElementsContentTypes.TextareaElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormTextAreaWithDescription
          v-if="element.contentType === FormElementsContentTypes.TextareaWithDescriptionElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element as FormTextareaWithDescriptionElement"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormCheckbox
          v-if="
            element.contentType === FormElementsContentTypes.ChoiceElementBlock && element.properties.allowMultiSelect
          "
          v-model="inputModels[element.key]"
          :form-element="element"
        />
        <FormRadio
          v-if="
            element.contentType === FormElementsContentTypes.ChoiceElementBlock && !element.properties.allowMultiSelect
          "
          v-model="inputModels[element.key]"
          :form-element="element"
          :tooltip="element.properties?.description"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormCheckboxWithDescription
          v-if="
            element.contentType === FormElementsContentTypes.MultiOrSingleChoiceWithDescription &&
            element.properties.allowMultiSelect
          "
          v-model="inputModels[element.key]"
          :form-element="element as FormChoiceWithDescriptionElement"
        />
        <FormRadioWithDescription
          v-if="
            element.contentType === FormElementsContentTypes.MultiOrSingleChoiceWithDescription &&
            !element.properties.allowMultiSelect
          "
          v-model="inputModels[element.key]"
          :form-element="element as FormChoiceWithDescriptionElement"
          :tooltip="element.properties?.description"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormDateTime
          v-if="element.contentType === FormElementsContentTypes.DateTimeElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormSelection
          v-if="element.contentType === FormElementsContentTypes.SelectionElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormItemsSelection
          v-if="element.contentType === FormElementsContentTypes.ItemsSelectionElementBlock"
          :form-element="element"
          :items="items || []"
        />
        <FormAutocomplete
          v-if="element.contentType === FormElementsContentTypes.AutoCompleteHiddenElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :product="product"
          :details="details"
          :family-page-id="familyPageId"
          :mail-to="mailTo"
          :contact-us-form-name="contactUsFormName"
          :order-number="orderNumber"
        />
        <FormCaptcha
          v-if="
            element.contentType === FormElementsContentTypes.HcaptchaElementBlock ||
            element.contentType === FormElementsContentTypes.HcaptchaValidElementBlock
          "
          ref="formCaptchaRefs"
          :element-key="element.key"
          :site-key="element.properties.siteKey"
          :error-message="inputErrors[element.key]?.message"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormSectionTitle
          v-if="element.contentType === FormElementsContentTypes.SectionTitleElementBlock"
          :form-element="element as FormSectionTitleElement"
        />
        <FormCountryDropdown
          v-if="element.contentType === FormElementsContentTypes.CountryDropdownElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormStateDropdown
          v-if="element.contentType === FormElementsContentTypes.StateDropdownElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
        <FormOverrideRecipientEmails
          v-if="element.contentType === FormElementsContentTypes.OverrideRecipientsEmailsElementBlock"
          v-model="inputModels[element.key]"
          :form-element="element"
          :error-message="inputErrors[element.key]"
          @update:error-message="updateInputError(element.key, $event)"
        />
      </template>
    </form>
    <div
      v-if="!notDisplayRequiredFieldsAsteriskLegend && anyRequiredFields"
      class="required-fields text-sm capitalize text-[--color-neutral-a3]"
    >
      * {{ $t("common.messages.required_fields") }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { difference } from "lodash";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useClientAuth } from "@/core/composables/useClientAuth";
import { globals } from "@/core/globals";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { FormElementsContentTypes } from "../enums";
import { FormSubmitter } from "../forms-sdk";
import { FormServerValidationError, FormValidationError } from "../utils/formValidationError";
import { scrollToFirstFormError } from "../utils/scrollToFirstFormError";
import FormAutocomplete from "./form-autocomplete.vue";
import FormCaptcha from "./form-captcha.vue";
import FormCheckboxWithDescription from "./form-checkbox-with-description.vue";
import FormCheckbox from "./form-checkbox.vue";
import FormCountryDropdown from "./form-country-dropdown.vue";
import FormDateTime from "./form-date-time.vue";
import FormExpandableOptionalText from "./form-expandable-optional-text.vue";
import FormItemsSelection from "./form-items-selection.vue";
import FormOverrideRecipientEmails from "./form-override-recipient-emails.vue";
import FormRadioWithDescription from "./form-radio-with-description.vue";
import FormRadio from "./form-radio.vue";
import FormSectionTitle from "./form-section-title.vue";
import FormSelection from "./form-selection.vue";
import FormStateDropdown from "./form-state-dropdown.vue";
import FormTextAreaWithDescription from "./form-text-area-with-description.vue";
import FormTextArea from "./form-text-area.vue";
import FormText from "./form-text.vue";
import type {
  ElementValidationResult,
  FormContainer,
  FormSubmission,
  FormSubmitModel,
  FormValidationResult,
} from "../forms-sdk";
import type {
  ExtendedFormContainer,
  ExtendedFormElement,
  FormElementCondition,
  FormSectionTitleElement,
  FormTextareaWithDescriptionElement,
  ExpandableOptionalTextElement,
  FormChoiceWithDescriptionElement,
} from "../types";
import type { Product } from "@/core/api/graphql/types";

interface IProps {
  form?: ExtendedFormContainer;
  formName?: string;
  product?: Product;
  details?: string;
  familyPageId?: number;
  items?: string[];
  mailTo?: string;
  notDisplayRequiredFieldsAsteriskLegend?: boolean;
  contactUsFormName?: string;
  orderNumber?: string;
  noLeadingTitle?: boolean;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();

interface IEmits {
  (event: "submitted", value: FormSubmitModel): void;
}

// used to re-render the form after submit
const formKey = ref(0);

const inputModels = ref<Record<string, string>>({});
const inputErrors = ref<Record<string, ElementValidationResult>>({});
const { t } = useI18n();
const { gtmSendFormStartEvent } = useThorlabsGtm();

// Track if form_start event has been sent
const formStarted = ref(false);
const trackedFormElement = ref<HTMLFormElement | null>(null);

function updateInputError(key: string, error: ElementValidationResult) {
  inputErrors.value[key] = error;
}

const formCaptchaRefs = ref<InstanceType<typeof FormCaptcha>[] | null>(null);
const formValidationResult = ref<FormValidationResult[]>([]);

const elementMatchesCondition = (element: ExtendedFormElement, condition: FormElementCondition): boolean => {
  const modelValue = inputModels.value[condition.field];
  switch (condition.operator) {
    case "Equals":
      return modelValue?.toString() === condition.fieldValue?.toString();
    case "NotEquals":
      return modelValue?.toString() !== condition.fieldValue?.toString();
    case "Contains":
      return modelValue?.includes(condition.fieldValue?.toString());
    case "NotContains":
      return !modelValue?.includes(condition.fieldValue);
    case "MatchRegularExpression": {
      const regex = new RegExp(condition.fieldValue);
      return regex.test(modelValue);
    }
    default:
      return true;
  }
};

const elementMatchesConditions = (element: ExtendedFormElement): boolean => {
  if (!element.properties.conditions || element.properties.conditions.length === 0) {
    return true;
  }

  if (element.properties.conditionCombination === "Any") {
    return element.properties.conditions.some((condition) => elementMatchesCondition(element, condition));
  }

  return element.properties.conditions.every((condition) => elementMatchesCondition(element, condition));
};

function shouldShow(element: ExtendedFormElement) {
  if (!element.properties.satisfiedAction) {
    return true;
  }
  const conditionsAreMatched = elementMatchesConditions(element);

  return element.properties.satisfiedAction === "show" ? conditionsAreMatched : !conditionsAreMatched;
}

const displayedElements = computed(() => {
  if (!props.form?.formElements) {
    return [];
  }
  return props.form.formElements.map((e) => e as ExtendedFormElement).filter((e) => shouldShow(e));
});

const interactiveElements = computed(() => {
  // Filter out non-interactive elements (section titles, hidden autocomplete fields)
  return displayedElements.value.filter((element) => {
    const contentType = element.contentType;
    return (
      contentType !== (FormElementsContentTypes.SectionTitleElementBlock as string) &&
      contentType !== (FormElementsContentTypes.AutoCompleteHiddenElementBlock as string)
    );
  });
});

const anyRequiredFields = computed(
  () =>
    !!props.form?.formElements
      .map((e) => e as ExtendedFormElement)
      .find((e) => e.properties.validators.find((v) => v.type === "RequiredValidator")),
);

function resetForm(): void {
  if (!props.form) {
    return;
  }
  const formElement = document.getElementById(props.form.key) as HTMLFormElement;
  formElement.reset();
  removeFormInteractionListeners();
  inputModels.value = {};
  formKey.value++;
  formStarted.value = false;
  void nextTick(() => {
    attachFormInteractionListeners();
  });
}

const requiredFields = computed(() => {
  return props.form?.formElements
    .map((e) => e as ExtendedFormElement)
    .filter((e) => e.properties.validators.find((v) => v.type === "RequiredValidator"));
});

async function submit() {
  if (!props.form) {
    return;
  }

  const formElement = document.getElementById(props.form.key) as HTMLFormElement;
  const formData = new FormData(formElement);
  const values: FormSubmission[] = [];

  // Group multiple checkbox values by key
  const groupedValues = new Map<string, string[]>();
  formData.forEach((value, key) => {
    if (!groupedValues.has(key)) {
      groupedValues.set(key, []);
    }
    groupedValues.get(key)!.push(value as string);
  });

  // Convert grouped values to FormSubmission array
  groupedValues.forEach((valueArray, key) => {
    // For multiple values with the same key, join them with comma
    const value = valueArray.length > 1 ? valueArray.join(", ") : valueArray[0];
    values.push({ elementKey: key, value });
  });

  const formSubmitter = new FormSubmitter(props.form as FormContainer, globals.optimizelyUrl + "/");

  formValidationResult.value = formSubmitter.doValidate(values);

  // This is needed for Radio elements to show required validation errors
  // If no Radio button options are selected, the formData will not contain their keys
  const shownRequiredFields = computed(() => requiredFields?.value?.filter((field) => shouldShow(field)));
  if (
    difference(
      shownRequiredFields?.value?.map((e) => e.key),
      formValidationResult.value.filter((e) => !e.result.valid).map((e) => e.elementKey),
    ).length > 0
  ) {
    shownRequiredFields?.value?.forEach((field) => {
      if (!formValidationResult.value.find((e) => e.elementKey === field.key)) {
        formValidationResult.value.push({
          elementKey: field.key,
          result: { valid: false, message: "This field is required." },
        });
      }
    });
  }

  if (formValidationResult.value.find((e) => e.result.message)) {
    formValidationResult.value.forEach((result) => {
      inputErrors.value[result.elementKey] = result.result;
    });

    await nextTick();
    scrollToFirstFormError(formElement);

    throw new FormValidationError();
  }

  const accessToken = await getAccessToken();

  const formSubmitModel: FormSubmitModel = {
    formKey: props.form.key,
    locale: props.form.locale,
    isFinalized: true,
    partialSubmissionKey: "",
    hostedPageUrl: window.location.href,
    submissionData: values,
    accessToken: accessToken,
    currentStepIndex: 0,
  };

  // logic to handle result
  let serverFieldError: { elementKey: string; result: ElementValidationResult } | undefined;
  try {
    await formSubmitter.doSubmit(formSubmitModel);
    emit("submitted", formSubmitModel);
    resetForm();
  } catch (error) {
    const errorParts = (error as { detail: string }).detail.split(":");
    if (errorParts.length === 2) {
      const elementKey = errorParts[0];
      const messageKey = errorParts[1];
      serverFieldError = { elementKey, result: { valid: false, message: t(messageKey) } };
      inputErrors.value[elementKey] = serverFieldError.result;
    } else {
      throw error;
    }
  } finally {
    if (formCaptchaRefs.value?.length) {
      formCaptchaRefs.value[0].reset();
    }
  }

  if (serverFieldError) {
    // The captcha reset above emits a "valid" result, so re-apply the server error before bringing it into view
    inputErrors.value[serverFieldError.elementKey] = serverFieldError.result;
    await nextTick();
    scrollToFirstFormError(formElement);

    throw new FormServerValidationError(serverFieldError.elementKey);
  }
}

const { authorize } = useClientAuth();

async function getAccessToken(): Promise<string> {
  await authorize();
  const clientAuth = localStorage.getItem("clientAuth");
  if (clientAuth) {
    const clientAuthObject = JSON.parse(clientAuth);
    if (clientAuthObject.access_token) {
      return clientAuthObject.access_token;
    }
  }
  return "";
}

// Handle form start event on first user interaction (typing/changing)
const handleFormInteraction = (event: Event) => {
  if (formStarted.value || !props.form?.key) {
    return;
  }

  formStarted.value = true;

  // Capture first field details
  const target = event.target as HTMLElement;

  // Check if this is a captcha interaction (event dispatched on the form itself)
  const isCaptchaInteraction = target.tagName.toLowerCase() === "form";

  const firstFieldId = isCaptchaInteraction ? getCaptchaId() : getFieldId(target);
  const firstFieldName = isCaptchaInteraction ? "hCaptcha" : getFieldName(target);
  const firstFieldType = isCaptchaInteraction ? "hCaptcha" : getFieldType(target);
  const firstFieldPosition = isCaptchaInteraction ? getCaptchaPosition() : getFieldPosition(target);

  // Calculate form metadata
  const formDestination = getFormDestination();
  const formLength = getFormLength();

  gtmSendFormStartEvent(
    props.form.key,
    props.contactUsFormName ?? props.formName,
    firstFieldId,
    firstFieldName,
    firstFieldType,
    firstFieldPosition,
    formDestination,
    formLength,
  );
  removeFormInteractionListeners();
};

const getFieldType = (element: HTMLElement): string => {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "input") {
    return (element as HTMLInputElement).type || "text";
  }
  if (tagName === "select") {
    return "select";
  }
  if (tagName === "textarea") {
    return "textarea";
  }
  return tagName;
};

const getFieldName = (element: HTMLElement): string => {
  const fieldIdentifier = element.getAttribute("name") || element.id;
  if (!fieldIdentifier) {
    return "";
  }

  const formElement = interactiveElements.value.find((el) => el.key === fieldIdentifier);

  return formElement?.displayName || formElement?.properties?.label || fieldIdentifier;
};

const getFieldId = (element: HTMLElement): string => {
  const fieldIdentifier = element.getAttribute("name") || element.id;
  if (!fieldIdentifier) {
    return "";
  }

  const formElement = interactiveElements.value.find((el) => el.key === fieldIdentifier);

  return formElement?.key || fieldIdentifier;
};

const getFieldPosition = (element: HTMLElement): number => {
  if (!props.form?.formElements) {
    return 0;
  }

  // Get the field name or id (which corresponds to element.key)
  const fieldIdentifier = element.getAttribute("name") || element.id;
  if (!fieldIdentifier) {
    return 0;
  }

  // Find the position by matching the element key
  const position = interactiveElements.value.findIndex((el) => el.key === fieldIdentifier);

  return position >= 0 ? position + 1 : 0; // 1-based index
};

const getCaptchaPosition = (): number => {
  if (!props.form?.formElements) {
    return 0;
  }

  // Find the captcha element position
  const position = interactiveElements.value.findIndex(
    (el) =>
      el.contentType === (FormElementsContentTypes.HcaptchaElementBlock as string) ||
      el.contentType === (FormElementsContentTypes.HcaptchaValidElementBlock as string),
  );

  return position >= 0 ? position + 1 : 0; // 1-based index
};

const getCaptchaId = (): string => {
  if (!props.form?.formElements) {
    return "";
  }

  // Find the captcha element
  const captchaElement = interactiveElements.value.find(
    (el) =>
      el.contentType === (FormElementsContentTypes.HcaptchaElementBlock as string) ||
      el.contentType === (FormElementsContentTypes.HcaptchaValidElementBlock as string),
  );

  return captchaElement?.key || "hCaptcha";
};

const getFormDestination = (): string => {
  // Return the full page URL
  return window.location.href;
};

const getFormLength = (): number => {
  if (!props.form?.formElements) {
    return 0;
  }

  // Count visible, interactive form elements
  return interactiveElements.value.length;
};

const attachFormInteractionListeners = () => {
  if (!props.form?.key) {
    return;
  }

  const formElement = document.getElementById(props.form.key) as HTMLFormElement | null;
  if (!formElement) {
    return;
  }

  removeFormInteractionListeners();
  trackedFormElement.value = formElement;
  // Use { once: true } to automatically remove listeners after first event
  trackedFormElement.value.addEventListener("input", handleFormInteraction, { once: true });
  trackedFormElement.value.addEventListener("change", handleFormInteraction, { once: true });
};

const removeFormInteractionListeners = () => {
  if (!trackedFormElement.value) {
    return;
  }

  trackedFormElement.value.removeEventListener("input", handleFormInteraction);
  trackedFormElement.value.removeEventListener("change", handleFormInteraction);
  trackedFormElement.value = null;
};

onMounted(() => {
  void nextTick(() => {
    attachFormInteractionListeners();
  });
});

onUnmounted(() => {
  removeFormInteractionListeners();
});

defineExpose({
  resetForm,
  submit,
});
</script>
